import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Camera, Loader2, RefreshCcw, Check, AlertTriangle, SwitchCamera } from "lucide-react";
import { toast } from "sonner";
import { fileUrl } from "./ImageUpload";

/**
 * Live photo capture (no upload from disk). Uses getUserMedia + canvas.
 * Robust to devices without rear camera (falls back to any video input).
 */
export default function LivePhotoCapture({
  title,
  hint,
  facingMode = "user",
  onCapture,
  value,
  testid = "live-capture",
  aspect = "aspect-[4/3]",
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(false);
  const [currentFacing, setCurrentFacing] = useState(facingMode);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
      streamRef.current = null;
    }
    setActive(false);
  };

  async function tryGetUserMedia(facing) {
    if (!window.isSecureContext) {
      throw new Error("La caméra nécessite une connexion sécurisée (https). Ouvrez le site avec l'adresse https://…");
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("Votre navigateur ne supporte pas l'accès à la caméra. Essayez Chrome ou Safari récent.");
    }
    const attempts = [
      { audio: false, video: { facingMode: { ideal: facing }, width: { ideal: 1280 }, height: { ideal: 960 } } },
      { audio: false, video: true },
    ];
    let lastErr = null;
    for (const constraints of attempts) {
      try {
        return await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) { lastErr = e; }
    }
    // Dernier recours : choisir explicitement un périphérique vidéo disponible
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter((d) => d.kind === "videoinput");
      if (cams.length > 0) {
        return await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { deviceId: { exact: cams[0].deviceId } },
        });
      }
    } catch (e) { lastErr = lastErr || e; }
    const name = lastErr?.name || "";
    if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
      throw new Error("Accès caméra refusé. Autorisez la caméra pour ce site (icône 🔒 ou caméra dans la barre d'adresse, ou Réglages > Applications > Navigateur > Autorisations), puis réessayez.");
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") {
      throw new Error("Aucune caméra utilisable détectée sur cet appareil. Utilisez un téléphone ou un ordinateur avec webcam.");
    }
    if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") {
      throw new Error("La caméra est utilisée par une autre application ou un autre onglet. Fermez-les et réessayez.");
    }
    throw new Error(lastErr?.message || "Impossible d'activer la caméra. Vérifiez les permissions du navigateur.");
  }

  // Attache le flux à la balise <video> dès qu'elle est montée (corrige l'écran noir)
  const attachWhenReady = (s, tries = 0) => {
    const v = videoRef.current;
    if (v) {
      if (v.srcObject !== s) v.srcObject = s;
      const p = v.play();
      if (p && p.catch) p.catch(() => { /* autoplay bloqué : la vidéo démarre au 1er geste */ });
    } else if (tries < 30) {
      setTimeout(() => attachWhenReady(s, tries + 1), 60);
    }
  };

  const start = async (facing = currentFacing) => {
    setError(null);
    stopStream();
    try {
      const s = await tryGetUserMedia(facing);
      streamRef.current = s;
      setActive(true);
      setCurrentFacing(facing);
      attachWhenReady(s);
    } catch (e) {
      setError(e.message || "Accès caméra impossible.");
      setActive(false);
    }
  };

  const flip = () => start(currentFacing === "user" ? "environment" : "user");

  useEffect(() => () => stopStream(), []);

  const capture = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 960;
    if (!w || !h) { toast.error("Image vidéo non prête, réessayez."); return; }
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    setBusy(true);
    try {
      const blob = await new Promise((res) => c.toBlob((b) => res(b), "image/jpeg", 0.85));
      if (!blob) throw new Error("Capture impossible");
      const fd = new FormData();
      fd.append("file", new File([blob], "live.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onCapture(data.id);
      toast.success("Photo capturée");
      stopStream();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const retake = () => { onCapture(""); setTimeout(() => start(), 50); };
  const displayUrl = value ? fileUrl(value) : null;

  return (
    <div data-testid={testid}>
      {title && <p className="overline mb-2">{title}</p>}
      {hint && <p className="text-xs text-muted-foreground mb-3">{hint}</p>}

      {displayUrl ? (
        <div data-testid={`${testid}-preview`} className={`relative ${aspect} bg-black card-mc overflow-hidden`}>
          <img src={displayUrl} alt="" className="w-full h-full object-cover" />
          <div className="absolute top-2 left-2 bg-[hsl(var(--accent))] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm flex items-center gap-1">
            <Check className="w-3 h-3" /> Capturée
          </div>
          <button type="button" onClick={retake} data-testid={`${testid}-retake`} className="absolute top-2 right-2 bg-foreground text-background text-xs font-bold px-3 py-1.5 rounded-sm flex items-center gap-1">
            <RefreshCcw className="w-3 h-3" /> Reprendre
          </button>
        </div>
      ) : active ? (
        <div className={`relative ${aspect} bg-black card-mc overflow-hidden`}>
          <video ref={videoRef} playsInline autoPlay muted className="w-full h-full object-cover" />
          <div className="absolute inset-6 border-2 border-dashed border-white/60 pointer-events-none" />
          <button type="button" onClick={flip} data-testid={`${testid}-flip`} aria-label="Changer de caméra"
            className="absolute top-2 right-2 w-9 h-9 bg-black/60 text-white rounded-sm flex items-center justify-center">
            <SwitchCamera className="w-4 h-4" />
          </button>
          <button type="button" onClick={capture} disabled={busy} data-testid={`${testid}-shoot`}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 w-16 h-16 bg-white border-4 border-[hsl(var(--primary))] rounded-full flex items-center justify-center hover:scale-105 transition-transform">
            {busy ? <Loader2 className="w-6 h-6 animate-spin text-foreground" /> : <Camera className="w-6 h-6 text-foreground" />}
          </button>
        </div>
      ) : (
        <button type="button" data-testid={`${testid}-start`} onClick={() => start()}
          className={`${aspect} w-full card-mc flex flex-col items-center justify-center gap-2 hover:border-foreground transition-colors`}>
          <Camera className="w-7 h-7 text-foreground" />
          <span className="font-display font-bold">Activer la caméra</span>
          <span className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Prise de photo en direct uniquement</span>
        </button>
      )}

      {error && (
        <div className="mt-3 p-3 border border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 flex gap-2 items-start" data-testid={`${testid}-error`}>
          <AlertTriangle className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
          <div className="text-xs text-foreground">
            <p>{error}</p>
            <button type="button" onClick={() => start()} className="mt-2 underline font-bold" data-testid={`${testid}-retry`}>Réessayer</button>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
